import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { BRAND } from "../../data";
import { scrollToId } from "./primitives";

const LINKS = [
  { label: "Services", id: "services" },
  { label: "Why Us", id: "why" },
  { label: "Work", id: "work" },
  { label: "Contact", id: "contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-brand-black/90 backdrop-blur-md border-b border-brand-concrete" : "bg-transparent"
      }`}
    >
      <div className="flex items-center justify-between px-6 md:px-12 lg:px-16 h-20">
        <button data-testid="nav-logo" onClick={() => go("top")} className="flex items-center gap-3 group">
          <img src={BRAND.logo} alt={BRAND.full} className="h-11 md:h-12 w-auto object-contain transition-transform duration-300 group-hover:scale-105" />
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="font-mono-p text-xs uppercase tracking-[0.2em] text-white/90 hover:text-brand-red transition-colors duration-300 [text-shadow:0_1px_10px_rgba(0,0,0,0.7)]"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <a data-testid="nav-phone" href={BRAND.phoneHref} className="hidden lg:flex items-center gap-3 font-anton text-lg tracking-wide text-white hover:text-brand-red transition-colors duration-300">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-red opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-red" />
            </span>
            <span className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-brand-red" /> {BRAND.phone}
            </span>
          </a>
          <button data-testid="nav-menu-toggle" className="md:hidden text-white" onClick={() => setOpen(!open)}>
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          className="md:hidden bg-brand-asphalt border-t border-brand-concrete overflow-hidden"
        >
          <div className="flex flex-col px-6 py-4">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-mobile-${l.id}`}
                onClick={() => go(l.id)}
                className="py-3 text-left font-anton text-2xl tracking-wide border-b border-brand-concrete last:border-0"
              >
                {l.label}
              </button>
            ))}
            <a href={BRAND.phoneHref} className="py-4 flex items-center gap-2 text-brand-red font-anton text-2xl">
              <Phone className="w-5 h-5" /> {BRAND.phone}
            </a>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};
